import { Button, DatePicker, Input, type InputRef, Modal, Select } from "antd"
import dayjs from "dayjs"
import { useRef, useState } from "react"

import Flex from "#blocks/Flex"

import {
  CreatePortfolioStepInput,
  PortfolioMockStage,
} from "./usePortfolioDbStorage"

interface PortfolioStepModalProps {
  open: boolean
  projectId: string
  onClose: () => void
  onCreateStep: (input: CreatePortfolioStepInput) => void
}

const statusOptions: Array<{
  label: string
  value: PortfolioMockStage["status"]
}> = [
  { label: "Not Started", value: "not_started" },
  { label: "In Progress", value: "in_progress" },
  { label: "Review", value: "review" },
  { label: "Complete", value: "complete" },
  { label: "Has Feedback", value: "has_feedback" },
]

const PortfolioStepModal = ({
  open,
  projectId,
  onClose,
  onCreateStep,
}: PortfolioStepModalProps) => {
  const labelRef = useRef<InputRef>(null)
  const [label, setLabel] = useState("")
  const [dueDate, setDueDate] = useState<string | undefined>(undefined)
  const [status, setStatus] =
    useState<PortfolioMockStage["status"]>("not_started")
  const [medium, setMedium] = useState("")
  const [notes, setNotes] = useState("")
  const [teacherComment, setTeacherComment] = useState("")
  const [feedback, setFeedback] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")
  const [imageUrl, setImageUrl] = useState("")

  const resetAndClose = () => {
    setLabel("")
    setDueDate(undefined)
    setStatus("not_started")
    setMedium("")
    setNotes("")
    setTeacherComment("")
    setFeedback("")
    setRejectionReason("")
    setImageUrl("")
    onClose()
  }

  return (
    <Modal
      title="Add Portfolio Step"
      open={open}
      onCancel={resetAndClose}
      afterOpenChange={(visible) => {
        if (visible) labelRef.current?.focus()
      }}
      footer={[
        <Button key="cancel" onClick={resetAndClose}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          disabled={
            !label.trim() ||
            (status === "has_feedback" && !rejectionReason.trim())
          }
          onClick={() => {
            onCreateStep({
              projectId,
              label,
              dueDate,
              status,
              rejectionReason,
              medium,
              notes,
              teacherComment,
              feedback,
              imageUrl,
            })
            resetAndClose()
          }}
        >
          Create Step
        </Button>,
      ]}
    >
      <Flex col gap={10}>
        <Input
          ref={labelRef}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Step name (required)"
        />
        <DatePicker
          value={dueDate ? dayjs(dueDate) : null}
          onChange={(date) => setDueDate(date?.toISOString())}
          placeholder="Due date"
        />
        <Select options={statusOptions} value={status} onChange={setStatus} />
        {status === "has_feedback" && (
          <Input.TextArea
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            placeholder="Feedback reason (required)"
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        )}
        <Input
          value={medium}
          onChange={(event) => setMedium(event.target.value)}
          placeholder="Medium"
        />
        <Input.TextArea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Notes"
          autoSize={{ minRows: 2, maxRows: 5 }}
        />
        <Input.TextArea
          value={teacherComment}
          onChange={(event) => setTeacherComment(event.target.value)}
          placeholder="Teacher comment"
          autoSize={{ minRows: 2, maxRows: 5 }}
        />
        <Input.TextArea
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder="Feedback"
          autoSize={{ minRows: 2, maxRows: 5 }}
        />
        <Input
          value={imageUrl}
          onChange={(event) => setImageUrl(event.target.value)}
          placeholder="Image URL (optional)"
        />
      </Flex>
    </Modal>
  )
}

export default PortfolioStepModal
